import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { Button, Grid, Box, CircularProgress, Typography, Stack, Tooltip } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { RichTextEditor } from '@mantine/tiptap';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';

import { RootState } from '../../../reducers';
import { getSubjectiveAssessmentById, gotoNextMenu, gotoPreviousMenu, saveSubjectiveAssessment } from '../../../store/assessment.slice';
import AlertDialog from '../../AlertDialog/AlertDialog';
import { StatusEnum } from '../../../Enum/StatusEnum';
import { isAIIcon } from '../Assessment';
import { AssessmentQuestions } from '../../../utils/assessmentQuestions';
import InfoIcon from '@mui/icons-material/Info';
import HelpPage from '../../../assets/documents/Assessment_Current_Needs_Counseling_Subjective.pdf';

const classes = {
    title: {
        color: '#0063a7',
        marginTop: '10px',
    },
    richTextBox: {
        '& .mantine-RichTextEditor-content': {
            minHeight: '250px',
        },
    },
};

const SubjectiveAssessmentComponent: React.FC<{ memberId: string }> = ({ memberId }) => {
    const dispatch = useAppDispatch();
    const [showSaveAlert, setShowSaveAlert] = useState<boolean>(false);
    const [memberStates, setMemberStates] = useState('');
    const saveDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const skipDebouncedSaveRef = useRef(false);
    const { subjectiveAssessment, isFetching } = useSelector((state: RootState) => state.assessment);
    const { engagementDraft } = useSelector((state: RootState) => state.documentEngagement);

    const handleOnBlur = () => {
        if (saveDebounceRef.current) {
            clearTimeout(saveDebounceRef.current);
            saveDebounceRef.current = null;
        }
        dispatch(saveSubjectiveAssessment({ ...subjectiveAssessment, MemberId: memberId, MemberStates: memberStates, IsMemberStatesAi: false }));
    };

    const editor = useEditor({
        extensions: [StarterKit],
        content: memberStates,
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            setMemberStates(html);
        },
        onBlur: handleOnBlur,
    });

    useEffect(() => {
        if (skipDebouncedSaveRef.current) {
            skipDebouncedSaveRef.current = false;
            return;
        }
        if (!editor) return;

        if (saveDebounceRef.current) {
            clearTimeout(saveDebounceRef.current);
        }

        saveDebounceRef.current = setTimeout(() => {
            dispatch(saveSubjectiveAssessment({ ...subjectiveAssessment, MemberId: memberId, MemberStates: memberStates, IsMemberStatesAi: false }, false));
        }, 500);

        return () => {
            if (saveDebounceRef.current) {
                clearTimeout(saveDebounceRef.current);
                saveDebounceRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [memberStates]);

    useEffect(() => {
        if (Boolean(engagementDraft.SubjectiveAssessmentId)) {
            dispatch(getSubjectiveAssessmentById(engagementDraft.SubjectiveAssessmentId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        skipDebouncedSaveRef.current = true;
        setMemberStates(subjectiveAssessment.MemberStates || '');
        if (editor && subjectiveAssessment.MemberStates !== memberStates) {
            editor.commands.setContent(subjectiveAssessment.MemberStates || '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subjectiveAssessment.MemberStates]);

    const handleOnClickSave = () => {
        setShowSaveAlert(true);
    };

    const handleOnConfirmToNext = (isWithoutComplete = false) => {
        if (isWithoutComplete) {
            dispatch(saveSubjectiveAssessment({ ...subjectiveAssessment, Status: StatusEnum.Completed }, true));
        } else {
            dispatch(gotoNextMenu());
        }
    };

    return (
        <Grid container direction="column" spacing={2} className="content-panel">
            <Stack direction={'row'} alignItems={'center'} spacing={1} sx={{ ml: 2 }}>
                <Typography variant="h6">Subjective Assessment</Typography>
                <Tooltip title="Subjective Assessment Help Document">
                    <InfoIcon color="primary" fontSize="small" cursor="pointer" onClick={() => window.open(`${HelpPage}`, '_blank')} />
                </Tooltip>
            </Stack>
            {isFetching ? (
                <Grid className="content-form">
                    <Box display="flex" justifyContent="center" className="loader-container">
                        <CircularProgress />
                    </Box>
                </Grid>
            ) : (
                <>
                    <Grid className="content-form">
                        <Typography variant="h6" sx={classes.title}>
                            {subjectiveAssessment.IsMemberStatesAi && isAIIcon()} {AssessmentQuestions.SubjectiveAssessment_Statement}
                        </Typography>
                        <Box sx={classes.richTextBox}>
                            <RichTextEditor editor={editor}>
                                <RichTextEditor.Toolbar>
                                    <RichTextEditor.ControlsGroup>
                                        <RichTextEditor.Bold />
                                        <RichTextEditor.Italic />
                                        <RichTextEditor.Underline />
                                    </RichTextEditor.ControlsGroup>
                                </RichTextEditor.Toolbar>
                                <RichTextEditor.Content />
                            </RichTextEditor>
                        </Box>
                    </Grid>
                    <Grid justifyContent="flex-end" className="content-footer">
                        <Button
                            type="submit"
                            variant="contained"
                            color="secondary"
                            sx={{ mr: 3 }}
                            onClick={() => dispatch(gotoPreviousMenu())}
                            startIcon={<ArrowBackIosIcon />}
                        >
                            Previous
                        </Button>
                        <Button type="submit" variant="contained" endIcon={<ArrowForwardIosIcon />} onClick={handleOnClickSave}>
                            Next
                        </Button>
                    </Grid>
                </>
            )}
            {Boolean(engagementDraft.SubjectiveAssessmentId) ? (
                <AlertDialog
                    open={showSaveAlert}
                    title="Subjective Assessment"
                    cancelText="Go Back"
                    okText="Confirm"
                    onConfirm={() => handleOnConfirmToNext(true)}
                    onSkip={() => handleOnConfirmToNext()}
                    onClose={() => setShowSaveAlert(false)}
                    message="I confirm and acknowledge that Subjective assessment is completed."
                />
            ) : (
                <AlertDialog
                    open={showSaveAlert}
                    title="Subjective Assessment"
                    cancelText="No"
                    okText="Yes, Proceed"
                    onConfirm={() => handleOnConfirmToNext()}
                    onClose={() => setShowSaveAlert(false)}
                    message="Are you sure to proceed without completing Subjective assessment?"
                />
            )}
        </Grid>
    );
};

export default SubjectiveAssessmentComponent;
